import { signal } from '@preact/signals-core';
import { activeRoleSignal, getAuthHeaders } from './assets';
import { showToast } from '../utils/toast';
import { escapeHtml } from '../utils/sanitize';
import type { OpmTableColumn } from '../components/data_table';
import type { components } from '../api/v1';

type Asset = components['schemas']['Asset'];
type AssetInput = components['schemas']['AssetInput'];
type AssetUpdateInput = components['schemas']['AssetUpdateInput'];
type Customer = components['schemas']['Customer'];
type Site = components['schemas']['Site'];
type DeviceModel = components['schemas']['DeviceModel'];
type OwnershipType = 'owned' | 'rented';

type ValueElement = HTMLElement & { value?: string; checked?: boolean };
type OverlayElement = HTMLElement & { showOverlay?: () => void; hideOverlay?: () => void };

export const assetAdminAssetsSignal = signal<Asset[]>([]);
export const assetAdminCustomersSignal = signal<Customer[]>([]);
export const assetAdminSitesSignal = signal<Site[]>([]);
export const assetAdminModelsSignal = signal<DeviceModel[]>([]);
export const assetAdminLoadingSignal = signal<boolean>(false);
export const selectedAdminAssetIdSignal = signal<string | null>(null);

function readValue(form: ParentNode, name: string): string {
  const element = form.querySelector(`[name="${name}"]`) as ValueElement | null;
  return element?.value?.trim() || '';
}

function readChecked(form: ParentNode, name: string): boolean {
  const element = form.querySelector(`[name="${name}"]`) as ValueElement | null;
  return element?.checked === true;
}

function readNumber(value: string, fallback: number): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function readOptionalNumber(value: string): number | null {
  if (!value.trim()) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function ownershipFromValue(value: string): OwnershipType {
  return value === 'rented' ? 'rented' : 'owned';
}

async function fetchJson<T>(url: string): Promise<T | null> {
  const response = await fetch(url, { headers: getAuthHeaders() });
  if (!response.ok) return null;
  return await response.json() as T;
}

export async function refreshAssetAdminData(): Promise<void> {
  assetAdminLoadingSignal.value = true;
  try {
    const [assets, customers, sites, models] = await Promise.all([
      fetchJson<Asset[]>('/api/v1/assets'),
      fetchJson<Customer[]>('/api/v1/customers'),
      fetchJson<Site[]>('/api/v1/sites'),
      fetchJson<DeviceModel[]>('/api/v1/device-models'),
    ]);
    assetAdminAssetsSignal.value = assets || [];
    assetAdminCustomersSignal.value = customers || [];
    assetAdminSitesSignal.value = sites || [];
    assetAdminModelsSignal.value = models || [];
  } catch (error) {
    console.error('Failed to load asset administration data', error);
    showToast('Failed to load asset administration data.', 'error');
  } finally {
    assetAdminLoadingSignal.value = false;
  }
}

function renderSelect(label: string, name: string, options: string, id: string): string {
  return `
    <div style="display:flex; flex-direction:column; gap:0.25rem;">
      <label for="${id}" style="font-size:0.875rem; font-weight:500;">${escapeHtml(label)}</label>
      <select id="${id}" name="${name}" required style="padding:0.6rem; border:1px solid var(--p-color-border-subdued); border-radius:var(--p-border-radius-200, 8px); background:var(--p-color-bg-surface); color:var(--p-color-text); font:inherit; min-height:44px;">
        ${options}
      </select>
    </div>
  `;
}

function renderAssetForm(mode: 'create' | 'edit'): string {
  const customers = assetAdminCustomersSignal.value;
  const sites = assetAdminSitesSignal.value;
  const models = assetAdminModelsSignal.value;
  const prefix = mode === 'create' ? 'create' : 'edit';
  const customerOptions = customers.map((customer) => `<option value="${customer.id}">${escapeHtml(customer.name)}</option>`).join('');
  const modelOptions = models.map((model) => `<option value="${model.id}">${escapeHtml(`${model.vendor} ${model.modelName}`)}</option>`).join('');
  const siteOptions = sites.map((site) => `<option value="${site.id}" data-customer-id="${site.customerId}">${escapeHtml(site.name)}</option>`).join('');

  return `
    <s-stack gap="base">
      <s-text-field label="Serial Number" name="serialNumber" id="${prefix}-asset-serial" placeholder="e.g. SN-HP-9876" required></s-text-field>
      ${renderSelect('Device Model', 'modelId', modelOptions, `${prefix}-asset-model`)}
      ${renderSelect('Customer', 'customerId', customerOptions, `${prefix}-asset-customer`)}
      ${renderSelect('Site', 'siteId', siteOptions, `${prefix}-asset-site`)}
      ${renderSelect('Ownership', 'ownershipType', '<option value="owned">Owned</option><option value="rented">Rented</option>', `${prefix}-asset-ownership`)}
      <s-checkbox label="MPS managed asset" name="isMps" id="${prefix}-asset-is-mps"></s-checkbox>
      <s-stack direction="inline" gap="base" style="flex-wrap:wrap;">
        <s-number-field label="Polling Interval (seconds)" name="intervalSeconds" value="3600" min="60" required></s-number-field>
        <s-number-field label="Anomaly Sensitivity Multiplier" name="anomalySensitivityMultiplier" value="3.0" min="0.1" step="0.1" required></s-number-field>
      </s-stack>
      <s-stack direction="inline" gap="base" style="flex-wrap:wrap;">
        <s-number-field label="Latitude (optional)" name="latitude" step="any" inputmode="decimal"></s-number-field>
        <s-number-field label="Longitude (optional)" name="longitude" step="any" inputmode="decimal"></s-number-field>
      </s-stack>
      <s-banner id="${prefix}-asset-error" tone="critical" hidden></s-banner>
      <s-stack direction="inline" gap="small" style="justify-content:flex-end; flex-wrap:wrap; margin-top:0.5rem;">
        <s-button variant="primary" id="btn-submit-${prefix}-asset">${mode === 'create' ? 'Create Asset' : 'Save Changes'}</s-button>
        <s-button commandFor="${prefix}-asset-modal" command="--hide">Cancel</s-button>
      </s-stack>
    </s-stack>
  `;
}

export function renderAssetAdminPage(): string {
  const role = activeRoleSignal.value;
  const canManage = role === 'superadmin' || role === 'admin' || role === 'manager';

  return `
    <s-page heading="Asset Manager" subtitle="Register, classify, and maintain the printer asset master." inlineSize="large">
      ${canManage ? '<div slot="primaryAction"><s-button variant="primary" id="btn-open-create-asset">Add Asset</s-button></div>' : ''}
      ${!canManage ? '<s-banner tone="info">You have read-only access to asset administration.</s-banner>' : ''}
      <s-section>
        <s-box padding="none" background="surface" border="base" borderRadius="base" style="overflow:hidden;">
          <opm-data-table id="asset-admin-table" searchable="true" paginated="true" page-size="10" empty-message="No assets found." search-placeholder="Search by serial number, model, customer, or site..."></opm-data-table>
        </s-box>
      </s-section>
      <s-modal id="create-asset-modal" heading="Create New Asset">
        <s-form id="form-create-asset">${renderAssetForm('create')}</s-form>
      </s-modal>
      <s-modal id="edit-asset-modal" heading="Edit Asset Details">
        <s-form id="form-edit-asset">${renderAssetForm('edit')}</s-form>
      </s-modal>
      <s-modal id="retire-admin-asset-modal" heading="Retire Asset?">
        <s-stack gap="base">
          <s-text tone="critical">Retiring preserves service history and disables active polling for this asset.</s-text>
          <s-banner id="retire-admin-asset-error" tone="critical" hidden></s-banner>
          <s-stack direction="inline" gap="small" style="justify-content:flex-end; flex-wrap:wrap;">
            <s-button variant="primary" tone="critical" id="btn-confirm-retire-admin-asset">Retire Asset</s-button>
            <s-button commandFor="retire-admin-asset-modal" command="--hide">Cancel</s-button>
          </s-stack>
        </s-stack>
      </s-modal>
    </s-page>
  `;
}

function setError(id: string, message: string): void {
  const banner = document.getElementById(id);
  if (!banner) return;
  banner.textContent = message;
  banner.toggleAttribute('hidden', !message);
}

function showModal(id: string): void {
  const modal = document.getElementById(id) as OverlayElement | null;
  modal?.showOverlay?.();
}

function hideModal(id: string): void {
  const modal = document.getElementById(id) as OverlayElement | null;
  modal?.hideOverlay?.();
}

function setFormValue(form: ParentNode, name: string, value: string): void {
  const element = form.querySelector(`[name="${name}"]`) as ValueElement | null;
  if (element && 'value' in element) element.value = value;
}

function setFormChecked(form: ParentNode, name: string, checked: boolean): void {
  const element = form.querySelector(`[name="${name}"]`) as ValueElement | null;
  if (element && 'checked' in element) element.checked = checked;
}

function syncSiteOptions(form: ParentNode): void {
  const customerId = readValue(form, 'customerId');
  const siteSelect = form.querySelector('[name="siteId"]') as HTMLSelectElement | null;
  if (!siteSelect) return;
  let firstVisible = '';
  Array.from(siteSelect.options).forEach((option) => {
    const visible = !option.dataset.customerId || option.dataset.customerId === customerId;
    option.hidden = !visible;
    option.disabled = !visible;
    if (visible && !firstVisible) firstVisible = option.value;
  });
  if (!Array.from(siteSelect.options).some((option) => !option.disabled && option.value === siteSelect.value)) {
    siteSelect.value = firstVisible;
  }
}

function syncOwnershipWithMps(form: ParentNode): void {
  const isMps = readChecked(form, 'isMps');
  const ownership = form.querySelector('[name="ownershipType"]') as HTMLSelectElement | null;
  if (!ownership) return;
  if (!isMps) {
    ownership.value = 'owned';
    ownership.disabled = true;
  } else {
    ownership.disabled = false;
  }
}

function collectAssetInput(form: ParentNode): AssetInput {
  const isMps = readChecked(form, 'isMps');
  const input: AssetInput = {
    serialNumber: readValue(form, 'serialNumber'),
    modelId: readValue(form, 'modelId'),
    ownershipType: isMps ? ownershipFromValue(readValue(form, 'ownershipType')) : 'owned',
    customerId: readValue(form, 'customerId'),
    siteId: readValue(form, 'siteId'),
    intervalSeconds: readNumber(readValue(form, 'intervalSeconds'), 3600),
    anomalySensitivityMultiplier: readNumber(readValue(form, 'anomalySensitivityMultiplier'), 3),
    isMps,
  };
  const latitude = readOptionalNumber(readValue(form, 'latitude'));
  const longitude = readOptionalNumber(readValue(form, 'longitude'));
  if (latitude !== null) input.latitude = latitude;
  if (longitude !== null) input.longitude = longitude;
  return input;
}

function collectAssetUpdate(form: ParentNode): AssetUpdateInput {
  const base = collectAssetInput(form);
  return {
    serialNumber: base.serialNumber,
    modelId: base.modelId,
    ownershipType: ownershipFromValue(base.ownershipType),
    customerId: base.customerId,
    siteId: base.siteId,
    intervalSeconds: base.intervalSeconds,
    anomalySensitivityMultiplier: base.anomalySensitivityMultiplier,
    isMps: base.isMps,
    latitude: readOptionalNumber(readValue(form, 'latitude')),
    longitude: readOptionalNumber(readValue(form, 'longitude')),
  };
}

function validateAssetInput(input: AssetInput | AssetUpdateInput): string | null {
  if ('serialNumber' in input && !input.serialNumber?.trim()) return 'Serial number is required.';
  if (!input.modelId) return 'Select a device model.';
  if (!input.customerId) return 'Select a customer.';
  if (!input.siteId) return 'Select a site.';
  if (input.intervalSeconds !== undefined && input.intervalSeconds < 60) return 'Polling interval must be at least 60 seconds.';
  if (input.anomalySensitivityMultiplier !== undefined && input.anomalySensitivityMultiplier <= 0) return 'Sensitivity multiplier must be greater than zero.';
  return null;
}

function responseError(response: Response, fallback: string): Promise<string> {
  return response.json().then((body: { error?: string }) => body.error || fallback).catch(() => fallback);
}

function rerenderAssetAdmin(): void {
  window.dispatchEvent(new HashChangeEvent('hashchange'));
}

export function setupAssetAdminPageListeners(): void {
  const role = activeRoleSignal.value;
  const canManage = role === 'superadmin' || role === 'admin' || role === 'manager';
  const assets = assetAdminAssetsSignal.value;
  const models = new Map(assetAdminModelsSignal.value.map((model) => [model.id, `${model.vendor} ${model.modelName}`]));
  const customers = new Map(assetAdminCustomersSignal.value.map((customer) => [customer.id, customer.name]));
  const sites = new Map(assetAdminSitesSignal.value.map((site) => [site.id, site.name]));
  const table = document.getElementById('asset-admin-table') as HTMLElement & { columns?: OpmTableColumn[]; items?: Asset[] } | null;

  if (table) {
    table.columns = [
      { key: 'serialNumber', header: 'Serial Number', sortable: true, sortType: 'string' },
      { key: 'modelId', header: 'Model', sortable: true, sortType: 'string', render: (row) => escapeHtml(models.get((row as Asset).modelId) || 'Unknown model') },
      { key: 'customerId', header: 'Customer', sortable: true, sortType: 'string', render: (row) => escapeHtml(customers.get((row as Asset).customerId) || 'Unknown customer') },
      { key: 'siteId', header: 'Site', sortable: true, sortType: 'string', render: (row) => escapeHtml(sites.get((row as Asset).siteId) || 'Unknown site') },
      { key: 'isMps', header: 'Classification', render: (row) => (row as Asset).isMps ? '<s-badge tone="info">MPS</s-badge>' : '<s-badge tone="attention">Non-MPS</s-badge>' },
      { key: 'ownershipType', header: 'Ownership', sortable: true, sortType: 'string', render: (row) => escapeHtml((row as Asset).ownershipType) },
      { key: 'status', header: 'Status', sortable: true, sortType: 'string', render: (row) => `<s-badge tone="${(row as Asset).status === 'active' ? 'success' : (row as Asset).status === 'error' ? 'critical' : 'attention'}">${escapeHtml((row as Asset).status)}</s-badge>` },
      { key: 'actions', header: 'Actions', render: (row) => canManage ? `<s-button data-action="edit" data-id="${escapeHtml((row as Asset).id)}" size="slim">Edit</s-button>${(row as Asset).status !== 'retired' ? `<s-button data-action="retire" data-id="${escapeHtml((row as Asset).id)}" tone="critical" size="slim">Retire</s-button>` : ''}` : '<s-text tone="subdued">Read-only</s-text>' },
    ];
    table.items = assets;
    table.addEventListener('row-action', (event: Event) => {
      const detail = (event as CustomEvent<{ action: string; id: string }>).detail;
      const asset = assets.find((item) => item.id === detail.id);
      if (!asset) return;
      if (detail.action === 'edit') openEditAsset(asset);
      if (detail.action === 'retire') {
        selectedAdminAssetIdSignal.value = asset.id;
        setError('retire-admin-asset-error', '');
        showModal('retire-admin-asset-modal');
      }
    });
  }

  if (!canManage) return;
  const createForm = document.getElementById('form-create-asset');
  const editForm = document.getElementById('form-edit-asset');
  [createForm, editForm].forEach((form) => {
    form?.querySelector('[name="customerId"]')?.addEventListener('change', () => syncSiteOptions(form));
    form?.querySelector('[name="isMps"]')?.addEventListener('change', () => syncOwnershipWithMps(form));
    syncSiteOptions(form || document);
    syncOwnershipWithMps(form || document);
  });

  document.getElementById('btn-open-create-asset')?.addEventListener('click', () => {
    const form = document.getElementById('form-create-asset');
    if (!form) return;
    (form as HTMLFormElement).reset();
    setFormChecked(form, 'isMps', true);
    setFormValue(form, 'intervalSeconds', '3600');
    setFormValue(form, 'anomalySensitivityMultiplier', '3');
    setError('create-asset-error', '');
    syncSiteOptions(form);
    syncOwnershipWithMps(form);
    showModal('create-asset-modal');
  });

  document.getElementById('btn-submit-create-asset')?.addEventListener('click', async (event) => {
    event.preventDefault();
    const form = document.getElementById('form-create-asset');
    if (!form) return;
    const input = collectAssetInput(form);
    const validationError = validateAssetInput(input);
    if (validationError) {
      setError('create-asset-error', validationError);
      return;
    }
    try {
      const response = await fetch('/api/v1/assets', { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(input) });
      if (!response.ok) {
        setError('create-asset-error', await responseError(response, 'Failed to create asset.'));
        return;
      }
      hideModal('create-asset-modal');
      showToast('Asset created successfully.', 'success');
      await refreshAssetAdminData();
      rerenderAssetAdmin();
    } catch (error) {
      console.error('Failed to create asset', error);
      setError('create-asset-error', 'Network failure while creating asset.');
    }
  });

  document.getElementById('btn-submit-edit-asset')?.addEventListener('click', async (event) => {
    event.preventDefault();
    const id = selectedAdminAssetIdSignal.value;
    const form = document.getElementById('form-edit-asset');
    if (!id || !form) return;
    const input = collectAssetUpdate(form);
    const validationError = validateAssetInput(input);
    if (validationError) {
      setError('edit-asset-error', validationError);
      return;
    }
    try {
      const response = await fetch(`/api/v1/assets/${id}`, { method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify(input) });
      if (!response.ok) {
        setError('edit-asset-error', await responseError(response, 'Failed to update asset.'));
        return;
      }
      hideModal('edit-asset-modal');
      showToast('Asset updated successfully.', 'success');
      await refreshAssetAdminData();
      rerenderAssetAdmin();
    } catch (error) {
      console.error('Failed to update asset', error);
      setError('edit-asset-error', 'Network failure while updating asset.');
    }
  });

  document.getElementById('btn-confirm-retire-admin-asset')?.addEventListener('click', async () => {
    const id = selectedAdminAssetIdSignal.value;
    if (!id) return;
    try {
      const response = await fetch(`/api/v1/assets/${id}/retire`, { method: 'POST', headers: getAuthHeaders() });
      if (!response.ok) {
        setError('retire-admin-asset-error', await responseError(response, 'Failed to retire asset.'));
        return;
      }
      hideModal('retire-admin-asset-modal');
      showToast('Asset retired successfully.', 'success');
      await refreshAssetAdminData();
      rerenderAssetAdmin();
    } catch (error) {
      console.error('Failed to retire asset', error);
      setError('retire-admin-asset-error', 'Network failure while retiring asset.');
    }
  });
}

function openEditAsset(asset: Asset): void {
  const form = document.getElementById('form-edit-asset');
  if (!form) return;
  selectedAdminAssetIdSignal.value = asset.id;
  setFormValue(form, 'serialNumber', asset.serialNumber);
  setFormValue(form, 'modelId', asset.modelId);
  setFormValue(form, 'customerId', asset.customerId);
  setFormValue(form, 'siteId', asset.siteId);
  setFormValue(form, 'ownershipType', asset.ownershipType);
  setFormValue(form, 'intervalSeconds', String(asset.intervalSeconds || 3600));
  setFormValue(form, 'anomalySensitivityMultiplier', String(asset.anomalySensitivityMultiplier || 3));
  setFormValue(form, 'latitude', asset.latitude == null ? '' : String(asset.latitude));
  setFormValue(form, 'longitude', asset.longitude == null ? '' : String(asset.longitude));
  setFormChecked(form, 'isMps', !!asset.isMps);
  setError('edit-asset-error', '');
  syncSiteOptions(form);
  syncOwnershipWithMps(form);
  showModal('edit-asset-modal');
}
